/**
 * Offline Indicator Component
 * 
 * Shows a persistent, non-intrusive indicator when the user is offline.
 * Does NOT use toasts - just a small fixed status bar.
 */

import React from 'react';
import { WifiOff } from 'lucide-react';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { cn } from '@/lib/utils';

interface OfflineIndicatorProps {
  className?: string;
}

export function OfflineIndicator({ className }: OfflineIndicatorProps) {
  const { isOnline } = useNetworkStatus();

  // Don't render anything when online
  if (isOnline) {
    return null;
  }

  return (
    <div 
      className={cn(
        "fixed top-0 left-0 right-0 z-[100] bg-amber-500 text-amber-950 py-1.5 px-4",
        "flex items-center justify-center gap-2 text-sm font-medium",
        "animate-in slide-in-from-top duration-300",
        className
      )}
      role="status"
      aria-live="polite"
    >
      <WifiOff className="h-4 w-4" />
      <span>Ei verkkoyhteyttä – yhteys puuttuu</span>
    </div>
  );
}

/**
 * Inline micro-text for buttons/actions when offline
 */
export function OfflineMicroText({ className }: { className?: string }) {
  const { isOnline } = useNetworkStatus();

  if (isOnline) {
    return null;
  }

  return (
    <span 
      className={cn(
        "text-xs text-amber-600 font-medium",
        className
      )}
    >
      Yhteys puuttuu
    </span>
  );
}

/**
 * HOC-style wrapper to disable a button when offline
 * Use with Button components to automatically disable + show micro text
 */
interface OfflineAwareButtonProps {
  children: React.ReactNode;
  /** If true, button is disabled when offline */
  disableWhenOffline?: boolean;
  /** Show micro text below button when offline */
  showMicroText?: boolean;
  className?: string;
}

export function OfflineAwareWrapper({ 
  children, 
  disableWhenOffline = true,
  showMicroText = true,
  className 
}: OfflineAwareButtonProps) {
  const { isOnline } = useNetworkStatus();

  return (
    <div className={cn("flex flex-col items-center gap-1", className)}>
      {React.Children.map(children, child => {
        if (React.isValidElement(child) && disableWhenOffline && !isOnline) {
          return React.cloneElement(child as React.ReactElement<{ disabled?: boolean }>, {
            disabled: true,
          });
        }
        return child;
      })}
      {showMicroText && !isOnline && <OfflineMicroText />}
    </div>
  );
}
